import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Plus, Share2, Trash2, X } from 'lucide-react';
import { useProjectList, projectsApi } from './useProjects';
import { openProjectById } from './openProject';
import { useProjectsScreen } from './projectsScreen-store';
import { useCurrentProject } from './currentProject';
import { ProjectCover } from './ProjectCover';
import { ShareDialog } from './ShareDialog';

function rel(ts: number): string {
  const s = Math.max(1, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function ProjectsScreen(): JSX.Element | null {
  const open = useProjectsScreen((s) => s.open);
  const close = useProjectsScreen((s) => s.close);
  const { projects, refresh, loading } = useProjectList();
  const currentId = useCurrentProject((s) => s.projectId);
  const [title, setTitle] = useState('');
  const [shareId, setShareId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const canClose = Boolean(currentId) || projects.length > 0;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && canClose) close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canClose, close]);

  if (!open) return null;

  async function doOpen(id: string) {
    await openProjectById(id);
    await navigate({ to: '/p/$projectId', params: { projectId: id } });
    close();
  }
  async function openProject(id: string) {
    if (busy) return;
    setBusy(true); setError(null);
    try { await doOpen(id); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to open project'); }
    finally { setBusy(false); }
  }
  async function create() {
    if (busy || !title.trim()) return;
    setBusy(true); setError(null);
    try {
      const p = await projectsApi.create(title.trim());
      setTitle('');
      await refresh();
      await doOpen(p.id);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to create project'); }
    finally { setBusy(false); }
  }
  async function remove(id: string) {
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    try { await projectsApi.remove(id); await refresh(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to delete project'); }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-[var(--color-bg)] font-[var(--font-mono)] text-[var(--color-text)]">
      {/* pt-20 clears the fixed top-left account blob (ProfileMenu) */}
      <div className="mx-auto max-w-5xl px-8 pt-20 pb-14">
        {canClose && (
          <button onClick={close} aria-label="Close" className="absolute right-5 top-5 rounded-[8px] p-2 text-[var(--color-text-muted)] hover:bg-[var(--color-menu-hover)] hover:text-[var(--color-text)]"><X size={18} /></button>
        )}

        {/* masthead — styleguide vitrine voice */}
        <header className="mb-9">
          <div className="mb-2.5 text-[13px] uppercase tracking-[0.2em] text-[var(--color-accent-2)]">Vivarium · your projects</div>
          <h1 className="text-[34px] font-bold leading-none">Projects</h1>
          <p className="mt-3 max-w-[52ch] text-[15px] text-[var(--color-text-muted)]">
            Pick up where you left off, or grow something new.
          </p>
        </header>

        {/* new project — comfortable input + gradient primary */}
        <div className="mb-9 flex gap-2.5">
          <input
            value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void create()}
            placeholder="Name a new vivarium…"
            className="min-h-[48px] flex-1 rounded-[8px] border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-4 text-[15px] outline-none focus-visible:outline-2 focus-visible:outline focus-visible:outline-[var(--color-accent)]"
          />
          <button
            onClick={() => void create()} disabled={busy}
            className="flex min-h-[48px] items-center gap-2 rounded-[8px] px-5 text-[15px] font-semibold text-white transition-transform duration-150 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:hover:translate-y-0"
            style={{ background: 'var(--grad)', boxShadow: '0 12px 30px -14px var(--color-accent-2)' }}
          >
            <Plus size={17} /> New project
          </button>
        </div>

        {error && <p className="mb-4 text-[14px] text-[var(--color-danger)]">{error}</p>}
        {loading && <p className="text-[14px] text-[var(--color-text-muted)]">Loading…</p>}

        {!loading && projects.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-20 text-center text-[var(--color-text-muted)]">
            <p className="text-[16px]">An empty vivarium. What do you want to grow?</p>
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(232px,1fr))] gap-5">
            {projects.map((p) => (
              <div
                key={p.id}
                className="group overflow-hidden rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-1)] transition-all duration-150 hover:-translate-y-0.5 hover:border-[color-mix(in_oklab,var(--color-accent)_45%,var(--color-border))] hover:shadow-[var(--shadow-2)]"
              >
                <button onClick={() => void openProject(p.id)} className="block aspect-[4/3] w-full cursor-pointer overflow-hidden" aria-label={`Open ${p.title}`}>
                  <ProjectCover projectId={p.id} title={p.title} coverUpdatedAt={p.coverUpdatedAt} />
                </button>
                <div className="flex items-center gap-2 border-t border-[var(--color-border)] px-3 py-2.5">
                  <button onClick={() => void openProject(p.id)} className="min-w-0 flex-1 cursor-pointer text-left">
                    <div className="truncate text-[14px]">{p.title}</div>
                    <div className="mt-0.5 text-[11px] uppercase tracking-[0.1em] text-[var(--color-text-muted)]">{p.role} · {rel(p.updatedAt)}</div>
                  </button>
                  {p.role === 'owner' && (
                    <div className="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                      <button onClick={() => setShareId(p.id)} aria-label={`Share ${p.title}`} className="rounded-[6px] p-1.5 text-[var(--color-text-muted)] hover:bg-[var(--color-menu-hover)] hover:text-[var(--color-text)]"><Share2 size={15} /></button>
                      <button onClick={() => void remove(p.id)} aria-label={`Delete ${p.title}`} className="rounded-[6px] p-1.5 text-[var(--color-text-muted)] hover:bg-[var(--color-menu-hover)] hover:text-[var(--color-danger)]"><Trash2 size={15} /></button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {shareId && <ShareDialog projectId={shareId} onClose={() => setShareId(null)} />}
    </div>
  );
}
